import axios from "axios";
import env from "@/app/config/env";

/**
 * Bot mitigation for anonymous forms. The provider and site key come from the
 * server so a key can be rotated per environment without rebuilding the bundle;
 * when the server reports it disabled every call here is a no-op.
 */
type CaptchaConfig = {
  Enabled: boolean;
  Provider: string;
  SiteKey: string | null;
};

type RecaptchaApi = {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      appearance: string;
      callback: (token: string) => void;
      "error-callback": () => void;
    },
  ) => string;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    grecaptcha?: RecaptchaApi;
    turnstile?: TurnstileApi;
  }
}

// const RECAPTCHA = "recaptcha";

let configRequest: Promise<CaptchaConfig | null> | null = null;
let scriptRequest: Promise<void> | null = null;

function fetchConfig(): Promise<CaptchaConfig | null> {
  // Cached on the module: the widget script must not be re-fetched per submit.
  configRequest ??= axios
    .get(`${env.services.portal}/captcha/config`)
    .then((response) => response.data as CaptchaConfig)
    .catch(() => null);

  return configRequest;
}

function loadScript(src: string): Promise<void> {
  scriptRequest ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("captcha script failed to load"));
    document.head.appendChild(script);
  });

  return scriptRequest;
}

function executeRecaptcha(siteKey: string, action: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const api = window.grecaptcha;

    if (!api) {
      reject(new Error("recaptcha unavailable"));
      return;
    }

    api.ready(() => {
      api.execute(siteKey, { action }).then(resolve, reject);
    });
  });
}

function executeTurnstile(siteKey: string, action: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const api = window.turnstile;

    if (!api) {
      reject(new Error("turnstile unavailable"));
      return;
    }

    // Turnstile always needs a container, even when it never shows a challenge.
    const container = document.createElement("div");
    container.style.display = "none";
    document.body.appendChild(container);

    const cleanup = (widgetId: string) => {
      api.remove(widgetId);
      container.remove();
    };

    const widgetId = api.render(container, {
      sitekey: siteKey,
      action,
      appearance: "interaction-only",
      callback: (token: string) => {
        cleanup(widgetId);
        resolve(token);
      },
      "error-callback": () => {
        cleanup(widgetId);
        reject(new Error("turnstile failed"));
      },
    });
  });
}

/**
 * Header to merge into an anonymous write request. Resolves to an empty object
 * when bot mitigation is off or the provider is unreachable — the server-side
 * rate limit still bounds abuse, and a provider outage must not block the form.
 */
export async function captchaHeaders(
  action: string,
): Promise<Record<string, string>> {
  try {
    const config = await fetchConfig();

    if (!config?.Enabled || !config.SiteKey) {
      return {};
    }

    const isRecaptcha = config.Provider?.toLowerCase() !== "turnstile";

    await loadScript(
      isRecaptcha
        ? `https://www.google.com/recaptcha/api.js?render=${config.SiteKey}`
        : "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit",
    );

    const token = isRecaptcha
      ? await executeRecaptcha(config.SiteKey, action)
      : await executeTurnstile(config.SiteKey, action);

    return { "X-Captcha-Token": token };
  } catch {
    return {};
  }
}
